import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronDown, Crown } from 'lucide-react';
import { createRoom, joinRoom, subscribeToRoom, leaveRoom, registerPresence, getRoom, autoTransferHost, claimRejoinLink, reclaimHostOnReturn } from '../utils/roomManager';
import { CurrencyProvider } from '../utils/currencyContext';
import {
  amountInputProps,
  centsToInputString,
  currencySymbol,
  DEFAULT_CURRENCY,
  formatCurrency,
  getDefaultBuyInCents,
  parseCurrencyInput,
  viewerLocale,
} from '../utils/currency';
import { ensureAuth } from '../utils/auth';
import { getStoredSession, setStoredSession, clearStoredSession } from '../utils/sessionStore';
import { isLeft, isSeated, isViewerHost, seatedPlayers, findOwnSeat, nextConnectedHostId, msUntilHostHandoff } from '../utils/playerLedger';
import { ensureServerClock, serverNow } from '../utils/serverClock';
import { updateProfileName, incrementGamesPlayed, incrementHandsPlayed, recordGameEntry } from '../utils/userProfile';
import InviteSeatModal from './InviteSeatModal';
import { assignRoles, calculateBlinds, getFirstToAct } from '../utils/pokerLogic';
import { ref, get, update, serverTimestamp } from 'firebase/database';
import { blindsGoUpMinutes } from '../utils/blindsGoUp';
import { database } from '../firebase/firebaseConfig';
import LobbyOverlay from './LobbyOverlay';
import SettingsOverlay from './SettingsOverlay';
import GameBoard from './GameBoard';
import EndGameScreen from './EndGameScreen';
import Button from './ui/Button';
import Wordmark from './ui/Wordmark';
import Modal from './ui/Modal';
import CurrencyModal from './CurrencyModal';
import PokerTableDemo from './PokerTableDemo';
import FeedbackModal from './FeedbackModal';
import { trackGameStart, trackNewHand, getCachedTotalHandsDealt, getTotalHandsDealt, trackSessionStart, trackSessionEnd } from '../utils/analytics';
import { captureHandStarted, captureGameStarted } from '../utils/posthogAnalytics';
import { playerNamesFrom } from '../utils/shouldCountStats';
import { t } from '../i18n';
import { useLocale } from '../i18n/useT';
import { localizeError } from '../i18n/errors';

const fieldLabel = 'label-eyebrow mb-2';
const fieldInput =
  'w-full px-4 py-3 bg-transparent border border-line rounded-lg text-ink placeholder:text-mute focus:outline-none focus:border-ink transition-colors';


// Render a two-sentence string with an optional line break between sentences
// (break visibility controlled by brClass). Works for any locale.
function twoLines(text, brClass) {
  const parts = String(text).split(/(?<=[.。!?！？])\s*/).filter(Boolean);
  if (parts.length !== 2) return text;
  return (<>{parts[0]}<br className={brClass} /> {parts[1]}</>);
}

function Home() {
  useLocale();
  const { code: joinCodeFromUrl, token: rejoinTokenFromUrl } = useParams();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [prefilledRoomCode, setPrefilledRoomCode] = useState('');
  const [previewRoom, setPreviewRoom] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalHandsDealt, setTotalHandsDealt] = useState(() => getCachedTotalHandsDealt());

  // Room state
  const [currentRoom, setCurrentRoom] = useState(null);
  const [currentPlayerId, setCurrentPlayerId] = useState(null);
  const [isHost, setIsHost] = useState(false);

  // Pending rejoin prompt — set when a stored session resolves to a still-live
  // PLAYING room and this phone opened the homepage (not its own room link).
  const [pendingRejoin, setPendingRejoin] = useState(null);
  const [hostHandoffNotice, setHostHandoffNotice] = useState(null);
  const [rejoinWelcome, setRejoinWelcome] = useState(false);
  const prevHostIdRef = useRef(null);
  const zeroTapLockRef = useRef(false);

  // Set when this phone's seat went away without it leaving (the host
  // removed it): { roomCode }. Shown once on home as a small centered modal.
  const [offTableNotice, setOffTableNotice] = useState(null);
  // True while this phone is leaving on its own, so its own leave never
  // reads as "the host removed you". Reset when a room subscription starts.
  const leavingRef = useRef(false);
  // Set when the friend dismisses the invite sheet so a late getRoom /
  // subscribe snapshot cannot reopen it on the same /join/:code.
  const dismissedInviteRef = useRef('');

  // True once the mount-time session restore check has resolved. The deep-link
  // /join/:code auto-open waits on this so a stored session can win over the URL.
  // Initialized synchronously from localStorage so we know up-front whether to wait.
  const [sessionCheckDone, setSessionCheckDone] = useState(() => !getStoredSession());

  // Rooms older than this since lastActivity are treated as dead — silently discard
  // the stored session instead of prompting. The server-side sweep at
  // roomManager.js:~445 already reaps after ~10 min; this is a safety net.
  const STALE_REJOIN_MS = 30 * 60 * 1000;

  useEffect(() => {
    if (!rejoinWelcome) return;
    const welcomeTimer = setTimeout(() => setRejoinWelcome(false), 4000);
    return () => clearTimeout(welcomeTimer);
  }, [rejoinWelcome]);

  // /r/:code/:token — first phone in that seat; a used/invalid token opens join.
  useEffect(() => {
    if (!sessionCheckDone) return;
    if (!joinCodeFromUrl || !rejoinTokenFromUrl || currentRoom) return;
    const code = joinCodeFromUrl.toUpperCase().slice(0, 5);
    if (dismissedInviteRef.current === code) return;
    (async () => {
      if (zeroTapLockRef.current) return;
      zeroTapLockRef.current = true;
      try {
        const result = await claimRejoinLink(code, rejoinTokenFromUrl);
        // Apply even if Strict Mode re-runs the effect: a successful claim
        // spends the token, so bailing on `cancelled` would leave this
        // phone on home with a used link.
        if (result.ok) {
          setCurrentRoom(result.gameState);
          setCurrentPlayerId(result.playerId);
          const seat = result.gameState?.players?.[result.playerId];
          const joinedAsHost = isViewerHost(result.playerId, result.gameState?.hostId, result.gameState?.players);
          setIsHost(joinedAsHost);
          setShowJoinModal(false);
          setStoredSession({
            roomCode: result.roomCode,
            playerId: result.playerId,
            isHost: joinedAsHost,
            playerName: seat?.name || '',
          });
          if (seat?.name) updateProfileName(seat.name);
          recordGameEntry(result.roomCode);
          setRejoinWelcome(true);
          return;
        }
      } catch (err) {
        console.warn('Rejoin link failed:', err);
      }
      zeroTapLockRef.current = false;
      if (currentRoom) return;
      try {
        const room = await getRoom(code);
        if (room && room.status !== 'ENDED') {
          if (dismissedInviteRef.current === code) return;
          setPreviewRoom({ ...room, roomCode: room.roomCode || code });
          return;
        }
        if (room?.status === 'ENDED') {
          setError(t('error.gameEnded'));
        }
      } catch (err) {
        console.warn('Rejoin-token join preview failed:', err);
      }
      setPrefilledRoomCode(code);
      setShowJoinModal(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinCodeFromUrl, rejoinTokenFromUrl, sessionCheckDone, currentRoom]);

  // /join/:code — if this device already holds a seat, land in it with no
  // name entry. Otherwise open the invite sheet over home. Missing / ended
  // rooms still get the Join modal so they can retype the code.
  useEffect(() => {
    if (!joinCodeFromUrl) dismissedInviteRef.current = '';
    if (!sessionCheckDone) return;
    if (rejoinTokenFromUrl) return;
    if (!joinCodeFromUrl || currentRoom) return;
    const code = joinCodeFromUrl.toUpperCase().slice(0, 5);
    if (dismissedInviteRef.current === code) return;
    if (pendingRejoin && pendingRejoin.roomCode !== code) return;

    let cancelled = false;
    (async () => {
      if (pendingRejoin && pendingRejoin.roomCode === code) {
        if (zeroTapLockRef.current) return;
        zeroTapLockRef.current = true;
        const ok = await handleConfirmRejoin();
        if (!ok) zeroTapLockRef.current = false;
        return;
      }
      try {
        const uid = await ensureAuth();
        const room = await getRoom(code);
        if (cancelled) return;
        const mine = findOwnSeat(room?.players, uid);
        if (mine && isSeated(mine) && room.status !== 'ENDED') {
          if (zeroTapLockRef.current) return;
          zeroTapLockRef.current = true;
          const ok = await handleJoinGame(code, mine.name);
          if (ok) return;
          zeroTapLockRef.current = false;
        }
        if (room && room.status !== 'ENDED') {
          if (dismissedInviteRef.current === code) return;
          setPreviewRoom({ ...room, roomCode: room.roomCode || code });
          return;
        }
        if (room?.status === 'ENDED') {
          setError(t('error.gameEnded'));
        }
      } catch (err) {
        console.warn('Zero-tap join failed:', err);
        zeroTapLockRef.current = false;
      }
      if (cancelled || currentRoom) return;
      setPrefilledRoomCode(code);
      setShowJoinModal(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinCodeFromUrl, pendingRejoin, sessionCheckDone, currentRoom]);

  // Keep the invite sheet's player count live while the friend is still on home.
  useEffect(() => {
    if (!previewRoom?.roomCode || currentRoom) return undefined;
    const code = previewRoom.roomCode;
    return subscribeToRoom(code, (room) => {
      if (dismissedInviteRef.current === code) return;
      if (!room || room.status === 'ENDED') {
        setPreviewRoom(null);
        if (room?.status === 'ENDED') {
          setError(t('error.gameEnded'));
          setPrefilledRoomCode(code);
          setShowJoinModal(true);
        }
        return;
      }
      setPreviewRoom({ ...room, roomCode: room.roomCode || code });
    });
  }, [previewRoom?.roomCode, currentRoom]);

  // Restore on mount: if a previous session is stored AND the room is still
  // live AND the player record still exists, surface a Rejoin prompt instead
  // of silently dropping the user back into a (possibly forgotten) game.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = getStoredSession();
      if (!stored) {
        setSessionCheckDone(true);
        return;
      }
      try {
        const uid = await ensureAuth();
        const snapshot = await get(ref(database, `rooms/${stored.roomCode}`));
        if (cancelled) return;

        if (!snapshot.exists()) {
          clearStoredSession();
          return;
        }
        const roomData = snapshot.val();
        const player = roomData.players && roomData.players[stored.playerId];
        const stale = roomData.lastActivity && Date.now() - roomData.lastActivity > STALE_REJOIN_MS;
        const uidMatches = player && (
          player.id === uid ||
          player.controllerUid === uid
        );

        if (!player || !uidMatches || stale || roomData.status === 'ENDED' || isLeft(player)) {
          clearStoredSession();
          // A stored session means this phone never left on its own, so a
          // missing or cashed-out seat in a live room was the host's removal.
          const seatGone = !player?.id || isLeft(player);
          if (seatGone && !stale && roomData.status !== 'ENDED') {
            setOffTableNotice({ roomCode: stored.roomCode });
          }
          return;
        }

        const urlCode = (joinCodeFromUrl || '').toUpperCase().slice(0, 5);
        const ownRoomLink = urlCode && urlCode === stored.roomCode;
        const liveHost = isViewerHost(stored.playerId, roomData.hostId, roomData.players);

        if (roomData.status === 'PLAYING' && !ownRoomLink) {
          setPendingRejoin({
            roomCode: stored.roomCode,
            playerId: stored.playerId,
            isHost: liveHost,
            playerName: stored.playerName || player.name
          });
        } else {
          // LOBBY, or PLAYING via this room's own link/QR: land in the seat.
          setCurrentRoom({ ...roomData, roomCode: stored.roomCode });
          setCurrentPlayerId(stored.playerId);
          setIsHost(liveHost);
          if (ownRoomLink) {
            recordGameEntry(stored.roomCode);
          }
        }
      } catch (err) {
        console.warn('Session restore check failed:', err);
        clearStoredSession();
      } finally {
        if (!cancelled) setSessionCheckDone(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load total hands dealt on component mount
  useEffect(() => {
    const loadTotalHands = async () => {
      try {
        const total = await getTotalHandsDealt();
        if (Number.isFinite(total)) {
          setTotalHandsDealt(total);
        }
      } catch (error) {
        console.warn('Failed to load total hands dealt:', error);
      }
    };
    
    loadTotalHands();
  }, []);

  // Self-count games/hands for the local player against their own profile.
  // Driven off the synced room so every device counts itself (not host-only):
  // a LOBBY->PLAYING transition counts one game; each new handNumber counts one
  // hand. Refs guard against double counting across re-renders.
  const countedGameKeyRef = useRef(null);
  const countedHandRef = useRef({ roomCode: null, handNumber: 0 });
  useEffect(() => {
    if (!currentRoom?.roomCode) return;
    const me = currentPlayerId && currentRoom.players?.[currentPlayerId];
    if (!me || isLeft(me)) return;

    if (currentRoom.status === 'PLAYING') {
      const gameKey = `${currentRoom.roomCode}:${currentRoom.createdAt || ''}`;
      if (countedGameKeyRef.current !== gameKey) {
        countedGameKeyRef.current = gameKey;
        incrementGamesPlayed();
      }

      const handNumber = currentRoom.gameState?.handNumber || 0;
      const counted = countedHandRef.current;
      if (handNumber > 0 && (counted.roomCode !== currentRoom.roomCode || handNumber > counted.handNumber)) {
        countedHandRef.current = { roomCode: currentRoom.roomCode, handNumber };
        incrementHandsPlayed();
      }
    }
  }, [currentRoom?.roomCode, currentRoom?.status, currentRoom?.createdAt, currentRoom?.gameState?.handNumber, currentPlayerId, currentRoom?.players]);

  // Real-time room synchronization
  useEffect(() => {
    if (!currentRoom?.roomCode) return;

    console.log('🔄 Subscribing to room updates:', currentRoom.roomCode);
    leavingRef.current = false;

    const unsubscribe = subscribeToRoom(currentRoom.roomCode, (updatedRoom) => {
      if (updatedRoom) {
        console.log('🔄 Room updated:', updatedRoom);
        
        // Seat gone (lobby removal deletes it) or cashed out (mid-game
        // removal). If this phone didn't start a leave, the host removed it:
        // back to home with a short note.
        const me = currentPlayerId && updatedRoom.players?.[currentPlayerId];
        if (currentPlayerId && (!me?.id || isLeft(me))) {
          console.log('🔄 Current player no longer in room, returning to home');
          if (!leavingRef.current && updatedRoom.status !== 'ENDED') {
            setOffTableNotice({ roomCode: updatedRoom.roomCode || currentRoom.roomCode });
          }
          handleLeaveRoom(true); // Skip Firebase cleanup since player already removed / cashed out
          return;
        }
        
        setCurrentRoom(updatedRoom);
        if (currentPlayerId) {
          const nowHost = isViewerHost(currentPlayerId, updatedRoom.hostId, updatedRoom.players);
          setIsHost(nowHost);
          const stored = getStoredSession();
          if (
            stored
            && stored.playerId === currentPlayerId
            && stored.roomCode === (updatedRoom.roomCode || currentRoom.roomCode)
            && !!stored.isHost !== nowHost
          ) {
            setStoredSession({
              roomCode: stored.roomCode,
              playerId: stored.playerId,
              playerName: stored.playerName,
              isHost: nowHost,
            });
          }
        }
      } else {
        console.log('🔄 Room no longer exists');
        handleLeaveRoom(true); // Skip Firebase cleanup since room is gone
      }
    });

    // Cleanup subscription on unmount or room change
    return () => {
      console.log('🔄 Unsubscribing from room updates');
      unsubscribe();
    };
  }, [currentRoom?.roomCode, currentPlayerId]);

  // Soft-presence: stamp a lastSeen timestamp on disconnect instead of removing
  // the player. This is what makes refresh non-destructive — the player record
  // stays in Firebase, so the host doesn't see them vanish and the room (and
  // their seat) survives until they intentionally leave.
  useEffect(() => {
    if (!currentRoom?.roomCode || !currentPlayerId) return;

    let cancelPresence = () => {};
    registerPresence(currentRoom.roomCode, currentPlayerId).then(fn => {
      cancelPresence = fn;
    });

    return () => {
      cancelPresence();
    };
  }, [currentRoom?.roomCode, currentPlayerId]);

  // Auto-return: the host that auto-handoff moved away from is back, so this
  // device (and only this one) takes the table back. Idempotent.
  const myPresenceConnected = currentRoom?.players?.[currentPlayerId]?.presence?.connected;
  useEffect(() => {
    if (!currentRoom?.roomCode || currentRoom.status === 'ENDED' || !currentPlayerId) return;
    if (currentRoom.originalHostId !== currentPlayerId) return;
    if (myPresenceConnected !== true) return;
    reclaimHostOnReturn(currentRoom.roomCode, currentPlayerId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoom?.roomCode, currentRoom?.originalHostId, currentRoom?.hostId, currentRoom?.status, currentPlayerId, myPresenceConnected]);

  // Auto-handoff: if the host has been disconnected 2 min, the next connected
  // seat becomes host. The host's own device never runs this (it is connected).
  // expectedHostId + planAutoHostHandoff abort if another client already wrote.
  const hostPresence = currentRoom?.players?.[currentRoom?.hostId]?.presence;
  const connectedSeatKey = Object.values(currentRoom?.players || {})
    .filter((p) => p?.id && p.presence?.connected === true)
    .map((p) => p.id)
    .sort()
    .join(',');
  useEffect(() => {
    if (!currentRoom?.roomCode || currentRoom.status === 'ENDED' || !currentPlayerId) return;
    if (isViewerHost(currentPlayerId, currentRoom.hostId, currentRoom.players)) return;
    const host = currentRoom.players?.[currentRoom.hostId];
    if (!host || host.presence?.connected !== false) return;
    if (!nextConnectedHostId(currentRoom.players, currentRoom.hostId)) return;

    ensureServerClock();
    const wait = msUntilHostHandoff(host.presence, serverNow());
    const expectedHostId = currentRoom.hostId;
    const roomCode = currentRoom.roomCode;
    const timer = setTimeout(() => {
      autoTransferHost(roomCode, expectedHostId).catch((err) => {
        console.warn('Auto host handoff failed:', err);
      });
    }, wait);
    return () => clearTimeout(timer);
    // currentRoom is read inside but must not be a dep: a new snapshot every
    // action would reset the handoff timer and the handoff would never fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentRoom?.roomCode,
    currentRoom?.hostId,
    currentRoom?.status,
    currentPlayerId,
    hostPresence?.connected,
    hostPresence?.lastSeen,
    connectedSeatKey,
  ]);

  // New host after a disconnect: "You're the host now" / "<Name> disconnected."
  // Make host while the previous host is still connected does not show this.
  useEffect(() => {
    if (!currentRoom) {
      prevHostIdRef.current = null;
      return;
    }
    const prev = prevHostIdRef.current;
    const next = currentRoom.hostId;
    if (prev == null) {
      prevHostIdRef.current = next;
      return;
    }
    if (prev === next) return;
    prevHostIdRef.current = next;
    if (!isViewerHost(currentPlayerId, next, currentRoom.players)) return;
    const oldHost = currentRoom.players?.[prev];
    if (oldHost?.presence?.connected === false) {
      setHostHandoffNotice({ name: oldHost.name || t('home.theHost') });
    }
  }, [currentRoom, currentPlayerId]);

  const handleCreateGame = async (hostName, chipAmount, currency) => {
    if (!hostName.trim()) {
      setError(t('error.enterName'));
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const result = await createRoom(hostName.trim(), chipAmount, currency);
      
      setCurrentRoom(result.gameState);
      setCurrentPlayerId(result.hostId);
      setIsHost(true);
      setShowCreateModal(false);
      setStoredSession({
        roomCode: result.roomCode,
        playerId: result.hostId,
        isHost: true,
        playerName: hostName.trim()
      });
      updateProfileName(hostName.trim());
      recordGameEntry(result.roomCode);

      console.log('🎰 Room created successfully:', result.roomCode);
    } catch (error) {
      console.error('Failed to create room:', error);
      setError(t('error.failedCreate'));
    } finally {
      setLoading(false);
    }
  };

  const handleHostClick = () => {
    setError('');
    setShowCreateModal(true);
  };

  const handleJoinClick = () => {
    setError('');
    setPrefilledRoomCode('');
    setShowJoinModal(true);
  };

  const handleJoinGame = async (roomCode, playerName, buyInCents) => {
    if (!roomCode.trim() || !playerName.trim()) {
      setError(t('error.enterCodeAndName'));
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const options = Number.isFinite(buyInCents) ? { buyInCents } : undefined;
      const result = await joinRoom(roomCode.trim().toUpperCase(), playerName.trim(), options);
      
      setCurrentRoom(result.gameState);
      setCurrentPlayerId(result.playerId);
      const seat = result.gameState?.players?.[result.playerId];
      const joinedAsHost = isViewerHost(result.playerId, result.gameState?.hostId, result.gameState?.players);
      setIsHost(joinedAsHost);
      setShowJoinModal(false);
      setPreviewRoom(null);
      setStoredSession({
        roomCode: result.roomCode,
        playerId: result.playerId,
        isHost: joinedAsHost,
        playerName: seat?.name || playerName.trim()
      });
      updateProfileName(seat?.name || playerName.trim());
      recordGameEntry(result.roomCode);

      console.log('🎰 Joined room successfully:', result.roomCode);
      return true;
    } catch (error) {
      console.error('Failed to join room:', error);
      setError(localizeError(error.message) || t('error.generic'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveRoom = async (skipFirebaseCleanup = false) => {
    leavingRef.current = true;
    // 🔥 ANALYTICS: Track session end if leaving during an active game (host only)
    // Ends timing the actual poker game session when player exits during PLAYING state
    if (currentRoom && currentRoom.status === 'PLAYING' && isHost) {
      trackSessionEnd(playerNamesFrom(currentRoom.players));
    }
    
    if (currentRoom && currentPlayerId && !skipFirebaseCleanup) {
      try {
        const result = await leaveRoom(currentRoom.roomCode, currentPlayerId);
        if (result?.ok === false) {
          leavingRef.current = false;
          if (result.reason && result.reason !== 'no-successor') {
            setError(localizeError(result.reason));
          }
          return;
        }
        console.log('🎰 Successfully left room');
      } catch (error) {
        console.error('Failed to leave room cleanly:', error);
      }
    }
    
    clearStoredSession();
    setCurrentRoom(null);
    setCurrentPlayerId(null);
    setIsHost(false);
    setPreviewRoom(null);
    setError('');
  };

  const handleNotNow = () => {
    const code = (previewRoom?.roomCode || joinCodeFromUrl || '').toUpperCase().slice(0, 5);
    if (code) dismissedInviteRef.current = code;
    setPreviewRoom(null);
    setError('');
    setShowJoinModal(false);
    const params = new URLSearchParams(window.location.search);
    navigate(params.get('test') === '1' ? '/?test=1' : '/');
  };

  const handleConfirmRejoin = async () => {
    if (!pendingRejoin) return;
    setLoading(true);
    try {
      const snapshot = await get(ref(database, `rooms/${pendingRejoin.roomCode}`));
      if (!snapshot.exists()) {
        clearStoredSession();
        setPendingRejoin(null);
        return false;
      }
      const roomData = snapshot.val();
      const player = roomData.players && roomData.players[pendingRejoin.playerId];
      if (!player) {
        clearStoredSession();
        setPendingRejoin(null);
        return false;
      }
      setCurrentRoom({ ...roomData, roomCode: pendingRejoin.roomCode });
      setCurrentPlayerId(pendingRejoin.playerId);
      setIsHost(isViewerHost(pendingRejoin.playerId, roomData.hostId, roomData.players));
      setPendingRejoin(null);
      recordGameEntry(pendingRejoin.roomCode);
      return true;
    } catch (err) {
      console.error('Rejoin failed:', err);
      clearStoredSession();
      setPendingRejoin(null);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleStartGame = async (orderedIds = []) => {
    console.log('🎰 Starting game...');
    console.log('🎰 Current room:', currentRoom);
    console.log('🎰 Is host:', isHost);
    console.log('🎰 Current player ID:', currentPlayerId);
    
    if (!currentRoom || !isHost) {
      console.error('Cannot start game: not host or no room');
      return;
    }
    
    setError(''); // Clear any previous errors

    try {
      // Lobby players from Firebase (source of truth). Order comes from the
      // host's “What’s the order?” modal; anyone not in that list (joined
      // after the modal opened) is appended by joinedAt.
      const approvedPlayers = seatedPlayers(currentRoom.players);

      const idIndex = new Map((orderedIds || []).map((id, i) => [id, i]));
      approvedPlayers.sort((a, b) => {
        const ai = idIndex.has(a.id) ? idIndex.get(a.id) : Infinity;
        const bi = idIndex.has(b.id) ? idIndex.get(b.id) : Infinity;
        if (ai !== bi) return ai - bi;
        return (a.joinedAt || 0) - (b.joinedAt || 0);
      });
      
      if (approvedPlayers.length < 2) {
        setError(t('error.needTwo'));
        return;
      }

      console.log('🎰 Starting game with players:', approvedPlayers.map(p => p.name));

      // 1. Assign roles using pokerLogic.js
      const { roleAssignments, nextDealerId } = assignRoles(approvedPlayers, null);
      console.log('🎰 Role assignments:', roleAssignments);

      // Persist the chosen seating so GameBoard / turn order / new-hand
      // rotation (all sort by joinedAt) keep this order. Lobby never sees
      // these values — it unmounts when status becomes PLAYING.
      const seatBase = Date.now();

      // 2. Update players with their roles
      const playersWithRoles = approvedPlayers.map((player, index) => ({
        ...player,
        joinedAt: seatBase + index,
        role: roleAssignments[player.id] || 'PLAYER',
        currentBet: 0,
        handContribution: 0,
        hasFolded: false,
        isActive: true,
        isAllIn: false,
        hasActedThisRound: false
      }));

      // 3. Calculate blinds using game settings
      const { smallBlind, bigBlind } = currentRoom.settings;
      console.log('🎰 Full settings object:', currentRoom.settings);
      console.log('🎰 Settings:', { smallBlind, bigBlind });
      console.log('🎰 SmallBlind type:', typeof smallBlind, 'BigBlind type:', typeof bigBlind);
      console.log('🎰 SmallBlind isNaN:', isNaN(smallBlind), 'BigBlind isNaN:', isNaN(bigBlind));
      
      if (isNaN(smallBlind) || isNaN(bigBlind)) {
        console.error('🎰 Invalid blind amounts:', { smallBlind, bigBlind });
        throw new Error('Invalid blind settings - NaN detected');
      }
      
      const blindAmounts = calculateBlinds(playersWithRoles, smallBlind, bigBlind);
      console.log('🎰 Blind amounts:', blindAmounts);

      // 4. Post blinds and update player stacks/bets
      const playersWithBlinds = playersWithRoles.map(player => {
        const blindAmount = blindAmounts[player.id] || 0;
        console.log(`🎰 Processing ${player.name}: blindAmount=${blindAmount}, stack=${player.stack}`);
        
        if (blindAmount > 0) {
          const actualBlind = Math.min(blindAmount, player.stack); // Handle short stacks
          console.log(`🎰 ${player.name} posting blind: ${actualBlind} (stack before: ${player.stack}, after: ${player.stack - actualBlind})`);
          
          if (isNaN(actualBlind) || isNaN(player.stack)) {
            console.error(`🎰 NaN detected for ${player.name}:`, { actualBlind, stack: player.stack, blindAmount });
            throw new Error(`Invalid blind calculation for ${player.name}`);
          }
          
          return {
            ...player,
            currentRoundBet: actualBlind,      // Current betting round contribution
            totalPotContribution: actualBlind, // Total contribution to pot this hand
            stack: player.stack - actualBlind,
            isAllIn: actualBlind === player.stack,
            hasActedThisRound: false // Blind posters still need to act in pre-flop
          };
        }
        return {
          ...player,
          currentRoundBet: 0, // Explicitly set to 0 for non-blind players
          totalPotContribution: 0
        };
      });

      // 5. Calculate initial pot
      console.log('🎰 Players with blinds before pot calculation:', playersWithBlinds.map(p => ({
        name: p.name,
        currentRoundBet: p.currentRoundBet,
        stack: p.stack
      })));
      
      const totalPot = playersWithBlinds.reduce((sum, p) => {
        const bet = p.currentRoundBet || 0;
        console.log(`🎰 Adding ${bet} from ${p.name} to pot (current sum: ${sum})`);
        return sum + bet;
      }, 0);
      
      console.log('🎰 Total pot calculated:', totalPot);
      
      if (isNaN(totalPot)) {
        console.error('🎰 Total pot is NaN! Player bets:', playersWithBlinds.map(p => ({ name: p.name, bet: p.currentRoundBet })));
        throw new Error('Invalid pot calculation - NaN detected');
      }
      
      const mainPot = {
        id: 'main',
        amount: totalPot,
        eligiblePlayers: playersWithBlinds.filter(p => p.isActive).map(p => p.id),
        isMainPot: true
      };

      // 6. Determine first player to act (after blinds are posted)
      const firstToAct = getFirstToAct(playersWithBlinds);
      console.log('🎰 First to act:', firstToAct);

      // 7. Create players object for Firebase (keyed by ID)
      const playersObject = {};
      playersWithBlinds.forEach(player => {
        playersObject[player.id] = player;
      });

      // 8. Update Firebase with complete game state
      const gameStateUpdate = {
        status: 'PLAYING',
        players: playersObject,
        gameState: {
          stage: 'PRE_FLOP',
          dealerId: nextDealerId,
          currentTurnPlayerId: firstToAct,
          handNumber: 1,
          pots: [mainPot]
        },
        lastActivity: Date.now()
      };

      console.log('🎰 About to write to Firebase:', {
        mainPot: gameStateUpdate.gameState.pots[0],
        totalPot,
        playersWithBlinds: playersWithBlinds.map(p => ({
          name: p.name,
          currentRoundBet: p.currentRoundBet,
          stack: p.stack
        }))
      });

      // Update, don't replace the room: a full `set` would rewrite `settings`
      // from a possibly stale lobby snapshot and drop Away-blinds, currency,
      // and other host toggles that already landed in RTDB.
      const firstDealUpdate = { ...gameStateUpdate };
      let liveSettings = currentRoom.settings;
      try {
        const liveRoom = await getRoom(currentRoom.roomCode);
        if (liveRoom?.settings) liveSettings = liveRoom.settings;
      } catch (liveError) {
        console.warn('First deal using local settings; live room read failed:', liveError);
      }
      if (blindsGoUpMinutes(liveSettings) > 0) {
        firstDealUpdate['settings/blindsUpStartedAt'] = serverTimestamp();
      }
      await update(ref(database, `rooms/${currentRoom.roomCode}`), firstDealUpdate);

      // 🔥 ANALYTICS: Track game start event (host only)
      // Triggers when the first hand starts in a new game - increments games_started counter
      trackGameStart(isHost, playerNamesFrom(approvedPlayers));
      // PostHog: games started (stat #1). Same host-only gate as RTDB.
      captureGameStarted(currentRoom, approvedPlayers.length, smallBlind, bigBlind);

      // 🔥 ANALYTICS: Count the first hand of the game.
      // Subsequent hands are counted by GameBoard's "Start New Hand" handler.
      trackNewHand(isHost, playerNamesFrom(approvedPlayers));
      // PostHog: hands dealt. This is hand #1 — GameBoard's
      // deal-next-hand handler captures hands 2+.
      captureHandStarted(currentRoom, 1, approvedPlayers.length);
      
      // 🔥 ANALYTICS: Track game session start (host only)
      // Starts timing the actual poker game session - only counts PLAYING time
      if (isHost) {
        trackSessionStart();
      }

      console.log('🎰 Game started successfully!');
      console.log('🎰 Initial pot:', totalPot);
      console.log('🎰 Players with roles and blinds:', playersWithBlinds);

    } catch (error) {
      console.error('Failed to start game:', error);
      setError(t('error.failedStart'));
    }
  };

  const handleOpenSettings = () => {
    console.log('🎰 Opening settings...');
    setShowSettings(true);
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
  };



  // Show end game screen when game is over
  if (currentRoom?.status === 'ENDED') {
    return (
      <CurrencyProvider currency={currentRoom.settings?.currency}>
        <EndGameScreen
          gameState={currentRoom}
          onBackToHome={handleLeaveRoom}
        />
      </CurrencyProvider>
    );
  }

  // Show game board if game is active
  if (currentRoom && currentRoom.status === 'PLAYING') {
    return (
      <CurrencyProvider currency={currentRoom.settings?.currency}>
        <RejoinWelcomeToast show={rejoinWelcome} />
        <GameBoard
          gameState={currentRoom}
          currentPlayerId={currentPlayerId}
          isHost={isHost}
          onLeaveRoom={handleLeaveRoom}
        />
        
        {/* Settings Overlay */}
        {showSettings && (
          <SettingsOverlay
            gameState={currentRoom}
            currentPlayerId={currentPlayerId}
            onClose={handleCloseSettings}

          />
        )}
        <HostHandoffNotice
          notice={hostHandoffNotice}
          onClose={() => setHostHandoffNotice(null)}
        />
      </CurrencyProvider>
    );
  }

  // Show lobby if in a room but game not started.
  if (currentRoom) {
    return (
      <CurrencyProvider currency={currentRoom.settings?.currency}>
        <RejoinWelcomeToast show={rejoinWelcome} />
        <LobbyOverlay
          gameState={currentRoom}
          currentPlayerId={currentPlayerId}
          isHost={isHost}
          onStartGame={handleStartGame}
          onOpenSettings={handleOpenSettings}
          onLeaveRoom={handleLeaveRoom}
          error={localizeError(error)}
        />
        
        {/* Settings Overlay */}
        {showSettings && currentRoom && (
          <SettingsOverlay
            gameState={currentRoom}
            currentPlayerId={currentPlayerId}
            onClose={handleCloseSettings}

          />
        )}
        <HostHandoffNotice
          notice={hostHandoffNotice}
          onClose={() => setHostHandoffNotice(null)}
        />
      </CurrencyProvider>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-cream text-ink flex flex-col">
      {/* Top bar */}
      <header className="flex items-center px-5 pt-5 pb-2 sm:px-8 sm:pt-7">
        <Wordmark />
      </header>

      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 sm:px-8 py-10 text-center">
        <div className="w-full max-w-sm flex flex-col items-center gap-4">
          <h1 className="text-[40px] sm:text-[52px] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
            {t('home.heroTitle')}
          </h1>
          <p className="text-[14px] sm:text-[17px] text-mute leading-snug whitespace-normal min-[380px]:whitespace-nowrap">
            {twoLines(t('home.heroSub'), 'hidden max-[374px]:inline')}
          </p>

          {pendingRejoin && (
            <div className="w-full rounded-xl border border-line bg-cream-2 p-4 text-left" data-testid="rejoin-card">
              <p className="text-[14px] text-ink mb-3">
                {t('home.rejoinPrompt', { code: pendingRejoin.roomCode })
                  .split(pendingRejoin.roomCode)
                  .map((part, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <span className="tnum font-medium tracking-wide">{pendingRejoin.roomCode}</span>}
                      {part}
                    </React.Fragment>
                  ))}
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirmRejoin}
                disabled={loading}
                className="w-full"
                data-testid="rejoin-submit"
              >
                {t('home.rejoin')}
              </Button>
            </div>
          )}

          <div className="w-full flex flex-row gap-3">
            <Button
              variant="primary"
              size="lg"
              trailingArrow
              onClick={handleHostClick}
              disabled={loading}
              className="flex-1"
              data-testid="btn-host"
            >
              {t('home.host')}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={handleJoinClick}
              disabled={loading}
              className="flex-1"
              data-testid="btn-join"
            >
              {t('home.join')}
            </Button>
          </div>
          {Number.isFinite(totalHandsDealt) && (
            <div className="text-[13px] text-mute">
              {(() => {
                const raw = String(totalHandsDealt);
                const parts = t('home.handsDealt', { count: totalHandsDealt }).split(raw);
                return parts.map((part, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && (
                      <span className="tnum text-ink font-semibold">
                        {totalHandsDealt.toLocaleString()}
                      </span>
                    )}
                    {part}
                  </React.Fragment>
                ));
              })()}
            </div>
          )}
        </div>
      </main>

      <section className="w-full max-w-2xl mx-auto px-4 sm:px-8 pt-0 pb-12">
        <PokerTableDemo />
      </section>

      <section className="w-full max-w-2xl mx-auto px-6 sm:px-8 pb-12">
        <h2 className="text-[24px] sm:text-[28px] font-semibold tracking-[-0.02em] text-ink text-center">
          {t('home.howTitle')}
        </h2>
        <ol className="mt-6 space-y-4 text-[15px] sm:text-[16px] text-ink-2 leading-relaxed max-w-md mx-auto">
          <li className="flex items-start"><span className="text-mute tnum mr-3 flex-shrink-0">1.</span><span>{t('home.step1')}</span></li>
          <li className="flex items-start"><span className="text-mute tnum mr-3 flex-shrink-0">2.</span><span>{t('home.step2')}</span></li>
          <li className="flex items-start"><span className="text-mute tnum mr-3 flex-shrink-0">3.</span><span>{t('home.step3')}</span></li>
          <li className="flex items-start"><span className="text-mute tnum mr-3 flex-shrink-0">4.</span><span>{t('home.step4')}</span></li>
        </ol>
        {/* Unnumbered on purpose — the host role spans all four steps. The crown
            takes the number slot, matching the lobby's host indicator. */}
        <p className="mt-4 flex items-start text-[15px] sm:text-[16px] text-ink-2 leading-relaxed max-w-md mx-auto">
          <Crown className="w-4 h-4 text-mute mr-3 mt-[3px] flex-shrink-0" aria-label={t('common.host')} />
          <span>{t('home.hostRuns')}</span>
        </p>
        <div className="mt-8 mx-auto max-w-sm flex flex-row gap-3">
          <Button
            variant="primary"
            size="lg"
            trailingArrow
            onClick={handleHostClick}
            disabled={loading}
            className="flex-1"
          >
            {t('home.host')}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={handleJoinClick}
            disabled={loading}
            className="flex-1"
          >
            {t('home.join')}
          </Button>
        </div>
      </section>

      <footer className="pb-6 px-6 flex flex-col items-center">
        <div className="flex items-center justify-center gap-6">
          <Link to="/faq" className="text-[11px] text-mute/50 hover:text-mute transition-colors">{t('home.faq')}</Link>
          <Link to="/blog" className="text-[11px] text-mute/50 hover:text-mute transition-colors">{t('home.blog')}</Link>
          {/* Feedback keeps pill chrome — equal gap-6 still holds with the
              wider control, so it does not need to become a plain link. */}
          <button
            type="button"
            onClick={() => setShowFeedbackModal(true)}
            className="px-3.5 py-1.5 rounded-full border border-line text-[12px] font-medium text-ink hover:border-ink transition-colors"
          >
            {t('home.feedback')}
          </button>
          <a href="/privacy" className="text-[11px] text-mute/50 hover:text-mute transition-colors">{t('home.privacy')}</a>
        </div>
        <span className="mt-1.5 text-[11px] text-mute/50 whitespace-nowrap">© 2026 Michael Jonna</span>
      </footer>

      {showCreateModal && (
        <CreateGameModal
          onClose={() => {
            setShowCreateModal(false);
            setError('');
          }}
          onSubmit={handleCreateGame}
          loading={loading}
          error={localizeError(error)}
        />
      )}

      {showJoinModal && (
        <JoinGameModal
          onClose={() => {
            setShowJoinModal(false);
            setError('');
            setPrefilledRoomCode('');
          }}
          onSubmit={handleJoinGame}
          loading={loading}
          error={localizeError(error)}
          initialRoomCode={prefilledRoomCode}
        />
      )}

      {previewRoom && !showJoinModal && (
        <InviteSeatModal
          room={previewRoom}
          onClose={handleNotNow}
          onSubmit={handleJoinGame}
          loading={loading}
          error={localizeError(error)}
        />
      )}

      {showFeedbackModal && (
        <FeedbackModal onClose={() => setShowFeedbackModal(false)} source="footer" />
      )}

      <OffTableNotice
        open={!!offTableNotice}
        onClose={() => setOffTableNotice(null)}
      />
    </div>
  );
}

/** Centered notice when auto-handoff makes this device the host. */
export function HostHandoffNotice({ notice, onClose }) {
  return (
    <Modal
      open={!!notice}
      onClose={onClose}
      title={t('home.handoffTitle')}
      dismissible={false}
      testId="host-handoff-notice"
    >
      <p className="text-[14px] text-mute mb-6">
        {t('home.handoffBody', { name: notice?.name || t('home.theHost') })}
      </p>
      <button
        type="button"
        onClick={onClose}
        className="w-full h-12 rounded-[12px] bg-ink text-cream text-[14px] font-semibold"
        data-testid="host-handoff-ok"
      >
        {t('common.ok')}
      </button>
    </Modal>
  );
}

/** Small centered note after the host freed this phone's seat. */
export function OffTableNotice({ open, onClose }) {
  return (
    <Modal
      open={!!open}
      onClose={onClose}
      title={t('home.offTableTitle')}
      compact
      testId="off-table-notice"
    >
      <Button variant="primary" size="sm" onClick={onClose} className="w-full mt-2">
        {t('common.gotIt')}
      </Button>
    </Modal>
  );
}

export function RejoinWelcomeToast({ show }) {
  if (!show) return null;
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[70] px-5 py-2.5 rounded-full bg-ink text-cream text-[14px] font-medium whitespace-nowrap pointer-events-none"
      style={{ top: 'max(1.25rem, env(safe-area-inset-top))' }}
      data-testid="rejoin-welcome"
    >
      {t('home.rejoinWelcome')}
    </div>
  );
}

function CreateGameModal({ onClose, onSubmit, loading, error }) {
  // USD by default (not guessed from the phone); the host can switch it here.
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [pickerOpen, setPickerOpen] = useState(false);
  const loc = viewerLocale();
  const defaultBuyIn = getDefaultBuyInCents(currency);
  const [name, setName] = useState('');
  const [chipAmount, setChipAmount] = useState(() => centsToInputString(defaultBuyIn, currency, loc));
  // Until the host types their own amount, switching currency swaps in that
  // currency's default starting chips.
  const [chipsEdited, setChipsEdited] = useState(false);

  let parsedCents = 0;
  try {
    parsedCents = parseCurrencyInput(chipAmount, loc, currency);
  } catch {
    parsedCents = 0;
  }
  const submitDisabled = loading || !name.trim() || parsedCents < 100;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (parsedCents < 100) return;
    onSubmit(name, parsedCents / 100, currency);
  };

  const handlePickCurrency = (code) => {
    if (!chipsEdited) {
      setChipAmount(centsToInputString(getDefaultBuyInCents(code), code, loc));
    }
    setCurrency(code);
  };

  const handleChipAmountChange = (e) => {
    setChipsEdited(true);
    setChipAmount(e.target.value);
  };

  return (
    <>
    <Modal
      open
      onClose={onClose}
      title={t('home.hostModalTitle')}
      testId="create-game-modal"
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitDisabled}
            trailingArrow
            data-testid="create-submit"
          >
            {loading ? t('home.creating') : t('home.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div className={fieldLabel}>{t('home.yourName')}</div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('home.namePlaceholder')}
            className={fieldInput}
            maxLength={20}
            disabled={loading}
            autoComplete="off"
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck={false}
            autoFocus={false}
            data-testid="create-name"
          />
        </div>

        <div>
          <div className={fieldLabel}>{t('home.startingChips')}</div>
          <div className="relative">
            <input
              type="text"
              {...amountInputProps(currency)}
              value={chipAmount}
              onChange={handleChipAmountChange}
              placeholder={centsToInputString(defaultBuyIn, currency, loc)}
              className={`${fieldInput} tnum pr-28`}
              disabled={loading}
              data-testid="create-chips"
            />
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={loading}
              data-testid="create-currency"
              aria-label={t('home.currencyAria', { code: currency })}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full border border-line bg-cream-2 text-[12px] font-medium text-ink disabled:opacity-40"
            >
              <span className="tnum">{currencySymbol(currency, loc)}</span> {currency}
              <ChevronDown className="w-3 h-3" aria-hidden="true" />
            </button>
          </div>
          <div className="text-[12px] text-mute mt-1.5">
            {t('home.startingStack', { amount: formatCurrency(parsedCents >= 100 ? parsedCents : defaultBuyIn, currency, loc) })}
          </div>
        </div>

        {error && (
          <div className="text-[13px] text-accent-danger border border-accent-danger/30 rounded-lg px-3 py-2" data-testid="join-error">
            {error}
          </div>
        )}

        <button type="submit" hidden disabled={submitDisabled} />
      </form>
    </Modal>
    <CurrencyModal
      open={pickerOpen}
      selected={currency}
      onSelect={handlePickCurrency}
      onClose={() => setPickerOpen(false)}
    />
    </>
  );
}

function JoinGameModal({ onClose, onSubmit, loading, error, initialRoomCode = '' }) {
  const [roomCode, setRoomCode] = useState(initialRoomCode);
  const [name, setName] = useState('');
  const [roomLookup, setRoomLookup] = useState(null);

  useEffect(() => {
    if (initialRoomCode) {
      setRoomCode(initialRoomCode.toUpperCase().slice(0, 5));
    }
  }, [initialRoomCode]);

  useEffect(() => {
    const code = roomCode.trim().toUpperCase();
    if (code.length !== 5) {
      setRoomLookup(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const room = await getRoom(code);
        if (!cancelled) setRoomLookup(room);
      } catch {
        if (!cancelled) setRoomLookup(null);
      }
    })();
    return () => { cancelled = true; };
  }, [roomCode]);

  const submitDisabled = loading || !roomCode.trim() || !name.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit(roomCode, name);
  };

  const handleRoomCodeChange = (e) => {
    setRoomCode(e.target.value.toUpperCase().slice(0, 5));
  };

  const playingHint = roomLookup && roomLookup.status === 'PLAYING';

  return (
    <Modal
      open
      onClose={onClose}
      title={t('home.joinModalTitle')}
      testId="join-game-modal"
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitDisabled}
            trailingArrow
            data-testid="join-submit"
          >
            {loading ? t('home.joining') : t('home.join')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div className={fieldLabel}>{t('home.roomCode')}</div>
          <input
            type="text"
            value={roomCode}
            onChange={handleRoomCodeChange}
            placeholder="ABCDE"
            className={`${fieldInput} tnum text-center text-[22px] tracking-[0.3em] uppercase`}
            maxLength={5}
            disabled={loading}
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoFocus={false}
            data-testid="join-code"
          />
        </div>

        <div>
          <div className={fieldLabel}>{t('home.yourName')}</div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('home.namePlaceholder')}
            className={fieldInput}
            maxLength={20}
            disabled={loading}
            autoComplete="off"
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck={false}
            autoFocus={false}
            data-testid="join-name"
          />
          {playingHint && (
            <div className="text-[12px] text-mute mt-1.5">
              {t('home.gameInProgress')}
            </div>
          )}
        </div>

        {error && (
          <div className="text-[13px] text-accent-danger border border-accent-danger/30 rounded-lg px-3 py-2" data-testid="join-error">
            {error}
          </div>
        )}

        <button type="submit" hidden disabled={submitDisabled} />
      </form>
    </Modal>
  );
}

export default Home; 
